import SEOHead from '../../components/feature/SEOHead';
import { SITE_URL } from '../../utils/seo';
import { useState } from 'react';
import AnnouncementBar from '../home/components/AnnouncementBar';
import Navbar from '../home/components/Navbar';
import FooterSection from '../home/components/FooterSection';

const problemPoints = [
  'Depolama dizileri blokları tekilleştirir.',
  'Yedekleme sistemleri veri setlerini sıkıştırır.',
  'WAN iyileştiriciler iletim yükünü azaltır.',
];

const solutionFeatures = [
  {
    icon: 'ri-cpu-line',
    title: 'Yerel Entegrasyon',
    desc: 'Tekilleştirme, ayrı bir son işlem adımı olmaksızın tüm veri işlemlerinde küresel ve satır içi olarak çalışır.',
  },
  {
    icon: 'ri-database-2-line',
    title: 'Birleşik Meta Veri',
    desc: 'Tek, tutarlı bir tekilleştirme alanı tüm altyapı işlevlerini ve siteleri kapsar.',
  },
  {
    icon: 'ri-exchange-line',
    title: 'Katmanlar Arası İşlem',
    desc: 'Hiper yönetici kararları doğrudan depolama işlemlerini etkiler. Ağ transferleri mevcut tekilleştirme meta verilerinden otomatik olarak yararlanır.',
  },
];

const results = [
  { metric: '%40–60', label: 'G/Ç işlemlerinde azalma' },
  { metric: '2–3x', label: 'Önbellek isabet oranında artış' },
  { metric: '%70–90', label: 'WAN replikasyon trafiğinde azalma' },
  { metric: '%60–80', label: 'Daha kısa yedekleme pencereleri' },
  { metric: '5–10x', label: 'Daha hızlı kurtarma işlemleri' },
];

const customers = [
  { name: 'Topgolf', logo: 'https://public.readdy.ai/ai/img_res/70ab197727dfed8ec534d9db3f3c66cd.jpg' },
  { name: 'Lancaster Central School District', logo: 'https://readdy.ai/api/search-image?query=school%20district%20official%20seal%20emblem%20on%20white%20background%2C%20educational%20institution%20badge%2C%20circular%20emblem%20design&width=160&height=80&seq=lcd1&orientation=landscape' },
  { name: 'St. Clair County RESA', logo: 'https://readdy.ai/api/search-image?query=county%20regional%20education%20service%20agency%20logo%20on%20white%20background%2C%20government%20education%20agency%2C%20professional%20seal%20design&width=160&height=80&seq=resa1&orientation=landscape' },
];

const dedupFaqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { '@type': 'Question', name: 'Altyapı genelinde tekilleştirme nedir?', acceptedAnswer: { '@type': 'Answer', text: 'Geleneksel tekilleştirmenin aksine VergeOS, yalnızca depolama katmanında değil; sanallaştırma, ağ ve yedekleme işlemlerinde de veriyi tekilleştirerek sistem genelinde tek bir meta veri alanı oluşturur.' } },
    { '@type': 'Question', name: 'Neden depolama odaklı tekilleştirme yetersizdir?', acceptedAnswer: { '@type': 'Answer', text: 'Depolama odaklı tekilleştirme, veri her sistem sınırını geçtiğinde yeniden şişirir ve yeniden tekilleştirme gerektirir. Bu da %30-50 daha fazla CPU/RAM tüketimi ve uzayan yedekleme pencereleri anlamına gelir.' } },
    { '@type': 'Question', name: 'VergeOS tekilleştirme sorunlarını nasıl çözüyor?', acceptedAnswer: { '@type': 'Answer', text: "VergeOS, tekilleştirmeyi altyapı işletim sisteminin temel katmanlarına gömer. Veri sınır aşmadığından yeniden tekilleştirme olmaz; WAN'dan yedeklemeye kadar her katman aynı meta veriden yararlanır." } },
    { '@type': 'Question', name: 'Hangi iş yükleri en çok fayda sağlar?', acceptedAnswer: { '@type': 'Answer', text: 'Tekrarlayan veri setleri üreten yapay zeka iş yükleri, büyük ölçekli VM ortamları, yedekleme yoğun senaryolar ve çok siteli WAN replikasyonu en çok fayda sağlayan kullanım durumlarıdır.' } },
  ],
};

const dedupAppJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'VergeOS Tekilleştirme',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Any',
  description: 'Altyapı genelinde global satır içi tekilleştirme. G/Ç işlemlerinde %40-60 azalma, WAN replikasyon trafiğinde %70-90 düşüş.',
  url: `${SITE_URL}/deduplication`,
};

export default function DeduplicationPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqs = [
    {
      q: 'Altyapı genelinde tekilleştirme nedir?',
      a: 'Geleneksel tekilleştirmenin aksine VergeOS, yalnızca depolama katmanında değil; sanallaştırma, ağ ve yedekleme işlemlerinde de veriyi tekilleştirerek sistem genelinde tek bir meta veri alanı oluşturur.',
    },
    {
      q: 'Neden depolama odaklı tekilleştirme yetersizdir?',
      a: 'Depolama odaklı tekilleştirme, veri her sistem sınırını geçtiğinde yeniden şişirir ve yeniden tekilleştirme gerektirir. Bu da %30–50 daha fazla CPU/RAM tüketimi ve uzayan yedekleme penceleleri anlamına gelir.',
    },
    {
      q: 'VergeOS bu sorunları nasıl çözüyor?',
      a: 'VergeOS, tekilleştirmeyi altyapı işletim sisteminin temel katmanlarına gömer. Veri sınır aşmadığından yeniden tekilleştirme olmaz; WAN\'dan yedeklemeye kadar her katman aynı meta veriden yararlanır.',
    },
    {
      q: 'Hangi iş yükleri en çok fayda sağlar?',
      a: 'Tekrarlayan veri setleri üreten yapay zeka iş yükleri, büyük ölçekli VM ortamları, yedekleme yoğun senaryolar ve çok siteli WAN replikasyonu bu yaklaşımdan en çok fayda sağlayan kullanım durumlarıdır.',
    },
  ];

  return (
    <div className="min-h-screen w-full bg-white">
      <SEOHead
        title="Altyapı Genelinde Tekilleştirme | VergeOS"
        description="VergeOS ile depolama, sanallaştırma ve ağ genelinde global satır içi tekilleştirme. G/Ç işlemlerinde %40-60 azalma, WAN replikasyon trafiğinde %70-90 düşüş."
        keywords="tekilleştirme, deduplication, VergeOS, global deduplication, depolama optimizasyonu, WAN optimizasyonu"
        canonicalPath="/deduplication"
        jsonLd={[dedupAppJsonLd, dedupFaqJsonLd]}
      />
      <AnnouncementBar />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full overflow-hidden bg-[#0f1117] py-24 text-center">
          <div className="absolute inset-0 opacity-20"
            style={{ backgroundImage: 'repeating-linear-gradient(45deg,#e8600a 0,#e8600a 1px,transparent 0,transparent 50%)', backgroundSize: '20px 20px' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f1117]/60 to-[#0f1117]/90" />
          <div className="relative z-10 max-w-4xl mx-auto px-6">
            <div className="inline-block bg-[#e8600a]/20 text-[#e8600a] text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">
              Mimari
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Altyapı Genelinde<br />
              <span className="text-[#e8600a]">Tekilleştirme</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 font-medium">Depolamanın Ötesinde Tekilleştirme</p>
            <p className="text-gray-400 max-w-2xl mx-auto mb-10 text-sm leading-relaxed">
              VergeOS, depolama, sanallaştırma ve ağ genelinde yayılan altyapı geneli tekilleştirme sunar; hepsini birleşik bir platformda.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/architecture" className="bg-[#e8600a] hover:bg-[#cc5208] text-white font-semibold px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap">
                Mimari Genel Bakış
              </a>
            </div>
          </div>
        </section>

        {/* Intro Banner */}
        <section className="bg-gray-50 py-10 border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <p className="text-lg font-semibold text-[#1e293b] mb-2">
              Parçalı tekilleştirmenin gizli vergisini ödemeyi bırakın.
            </p>
            <p className="text-gray-600 text-sm">
              VergeOS, depolama, sanallaştırma ve ağı birleşik bir platformda kapsayan altyapı geneli tekilleştirme sunar.
            </p>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-bold text-[#1e293b] mb-6">Sadece Depolama Tekilleştirmesinin Sorunu</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Çoğu kuruluş tekilleştirme sorununu çözdüğünü düşünür. Ancak gerçekte her sistem bağımsız çalışır ve veri bir sınırı her geçtiğinde şişerek yeniden tekilleştirme zorunluluğu doğurur.
                </p>
                <ul className="space-y-3 mb-6">
                  {problemPoints.map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <span className="w-5 h-5 flex items-center justify-center mt-0.5 text-[#e8600a] shrink-0">
                        <i className="ri-checkbox-circle-line text-lg"></i>
                      </span>
                      <span className="text-gray-700 text-sm">{p}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
                  <p className="text-sm text-amber-900 leading-relaxed">
                    Sonuç? Kuruluşlar <strong>%30–50 daha fazla CPU ve RAM</strong> konuşlandırır, WAN devreleri gereksiz veri akışı taşır ve yedekleme pencereleri uzar.
                  </p>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=complex%20fragmented%20data%20deduplication%20system%20diagram%20showing%20multiple%20separate%20storage%20arrays%20backup%20systems%20and%20WAN%20optimizers%20each%20deduplicating%20independently%20with%20arrows%20showing%20data%20expanding%20contracting%20at%20boundaries%2C%20technical%20infrastructure%20diagram%20dark%20background%20orange%20highlights&width=580&height=420&seq=dedup_prob1&orientation=landscape"
                  alt="Tekilleştirme Sorunu Diyagramı"
                  className="w-full h-full object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* White Paper CTA */}
        <section className="bg-gray-50 border-y border-gray-200 py-8">
          <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-[#1e293b] mb-1">Teknik İnceleme: Entegre Tekilleştirme Üzerine Altyapı Oluşturma</p>
              <p className="text-sm text-gray-600">VergeOS tekilleştirmesinin neden farklı olduğuna derinlemesine bakış</p>
            </div>
            <a href="https://www.verge.io/white-papers/" target="_blank" rel="nofollow noreferrer"
              className="bg-[#e8600a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#cc5208] transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-download-2-line mr-2"></i>Şimdi İndir
            </a>
          </div>
        </section>

        {/* VergeOS Solution */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=unified%20infrastructure%20deduplication%20diagram%20showing%20VergeOS%20single%20deduplication%20domain%20spanning%20storage%20virtualization%20and%20networking%2C%20clean%20technical%20architecture%20diagram%20with%20orange%20and%20dark%20blue%20color%20scheme%2C%20data%20flowing%20seamlessly%20across%20unified%20platform&width=580&height=400&seq=dedup_sol1&orientation=landscape"
                  alt="VergeOS Tekilleştirme Çözümü"
                  className="w-full h-full object-top"
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-[#1e293b] mb-4">VergeOS: Altyapı Geneli Tekilleştirme</h2>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  VergeOS, bu verimsizlikleri altyapı işletim sistemine en başından yerleştirilmiş yerel tekilleştirme ile ortadan kaldırır. Kaynaklar için rekabet eden eklenti özellikler yerine tekilleştirme, depolama, sanallaştırma ve ağ genelinde birleşik bir yetenek olarak çalışır.
                </p>
                <div className="space-y-6">
                  {solutionFeatures.map((f) => (
                    <div key={f.title} className="flex gap-4">
                      <span className="w-10 h-10 flex items-center justify-center bg-[#e8600a]/10 rounded-lg text-[#e8600a] shrink-0">
                        <i className={`${f.icon} text-xl`}></i>
                      </span>
                      <div>
                        <p className="font-semibold text-[#1e293b] mb-1">{f.title}</p>
                        <p className="text-sm text-gray-600 leading-relaxed">{f.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Customer Results */}
        <section className="py-20 bg-[#f8fafc]">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-bold text-[#1e293b] mb-8">Müşteri Tarafından Doğrulanmış Sonuçlar</h2>
                <div className="grid grid-cols-1 gap-4 mb-8">
                  {results.map((r) => (
                    <div key={r.label} className="flex items-center gap-4 bg-white rounded-lg px-5 py-4 border border-gray-100">
                      <span className="text-2xl font-extrabold text-[#e8600a] whitespace-nowrap">{r.metric}</span>
                      <span className="text-sm text-gray-700">{r.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-widest mb-6">Güvenen Müşteriler</p>
                <div className="grid grid-cols-3 gap-4 mb-8">
                  {customers.map((c) => (
                    <div key={c.name} className="bg-white rounded-lg p-4 border border-gray-100 flex items-center justify-center min-h-[80px]">
                      <img src={c.logo} alt={c.name} className="max-h-12 object-contain" />
                    </div>
                  ))}
                </div>
                <div className="bg-white rounded-lg border border-gray-100 p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                  <div>
                    <p className="font-semibold text-[#1e293b] mb-1">Müşteri Vaka Çalışmaları</p>
                    <p className="text-sm text-gray-600">VergeIO müşterilerinin tekilleştirmeyi nasıl kullandığını görün</p>
                  </div>
                  <a href="https://www.verge.io/case-studies-registration-page/" target="_blank" rel="nofollow noreferrer"
                    className="bg-[#e8600a] text-white text-sm font-semibold px-5 py-2.5 rounded-md hover:bg-[#cc5208] transition-colors whitespace-nowrap cursor-pointer">
                    İndir
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why It Matters */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-8">Bu Neden Şimdi Önemli</h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Alternatif değerlendiren kuruluşlar, tüm altyapı yığınlarını yeniden düşünüyor. Yapay zeka iş yükleri, yalnızca depolama tekilleştirmesinin yetersiz kaldığı devasa tekrarlayan veri setleri üretiyor. Bütçe baskıları, parçalı yaklaşımların kaynak yükünü giderek daha az haklı kılıyor.
            </p>
            <p className="text-gray-600 mb-6 leading-relaxed">
              VergeOS, tekilleştirmeyi emtia bir depolama özelliğinden; performansı iyileştiren, maliyetleri düşüren ve yeni operasyonel kalıpları mümkün kılan stratejik bir altyapı yeteneğine dönüştürür.
            </p>
            <p className="font-semibold text-[#1e293b] mb-4">Tekilleştirme maliyetini ortadan kaldırmaya hazır mısınız?</p>
            <div className="flex flex-wrap gap-4">
              <a href="/iletisim"
                className="bg-[#e8600a] text-white font-semibold px-6 py-3 rounded-md hover:bg-[#cc5208] transition-colors cursor-pointer whitespace-nowrap">
                Teknik Sunum Planlayın
              </a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-gray-50 border-t border-gray-200">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-[#1e293b] text-center mb-12">Sıkça Sorulan Sorular</h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-lg border border-gray-100 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer"
                  >
                    <span className="font-medium text-[#1e293b] text-sm">{faq.q}</span>
                    <span className="w-5 h-5 flex items-center justify-center text-gray-400 shrink-0">
                      <i className={`${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg`}></i>
                    </span>
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <FooterSection />
    </div>
  );
}
